import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import PublicLayout from '../layouts/PublicLayout';
import DashboardLayout from '../layouts/DashboardLayout';
import ProtectedRoute from './ProtectedRoute';
import LoadingSpinner from '../components/LoadingSpinner';
import LabGuard from '../components/lab/LabGuard';

// Public Pages
const LandingPage = lazy(() => import('../pages/public/LandingPage'));
const DoctorListingPage = lazy(() => import('../pages/public/DoctorListingPage'));
const DoctorProfilePage = lazy(() => import('../pages/public/DoctorProfilePage'));
const LoginPage = lazy(() => import('../pages/public/LoginPage'));
const RegisterPage = lazy(() => import('../pages/public/RegisterPage'));
const PaymentPage = lazy(() => import('../pages/public/PaymentPage'));
const BookingConfirmationPage = lazy(() => import('../pages/public/BookingConfirmationPage'));

// Misc Pages
const NotFoundPage = lazy(() => import('../pages/NotFoundPage'));

// Customer Pages
const CustomerDashboard = lazy(() => import('../pages/customer/CustomerDashboard'));
const CustomerAppointments = lazy(() => import('../pages/customer/CustomerAppointments'));
const AppointmentDetail = lazy(() => import('../pages/customer/AppointmentDetail'));
const PaymentHistory = lazy(() => import('../pages/customer/PaymentHistory'));
const CustomerProfile = lazy(() => import('../pages/customer/CustomerProfile'));
const NotificationsPage = lazy(() => import('../pages/customer/NotificationsPage'));
const CustomerPrescriptions = lazy(() => import('../pages/customer/CustomerPrescriptions'));
const PrescriptionDetail = lazy(() => import('../pages/customer/PrescriptionDetail'));
const CustomerInvoices = lazy(() => import('../pages/customer/CustomerInvoices'));
const CustomerFeedback = lazy(() => import('../pages/customer/CustomerFeedback'));

// Hospital Admin Pages
const HospitalDashboard = lazy(() => import('../pages/hospital/HospitalDashboard'));
const HospitalProfile = lazy(() => import('../pages/hospital/HospitalProfile'));
const PaymentGatewaySettings = lazy(() => import('../pages/hospital/PaymentGatewaySettings'));
const InsuranceSearch = lazy(() => import('../pages/hospital/InsuranceSearch'));
const DoctorManagement = lazy(() => import('../pages/hospital/DoctorManagement'));
const AddEditDoctor = lazy(() => import('../pages/hospital/AddEditDoctor'));
const BranchManagement = lazy(() => import('../pages/hospital/BranchManagement'));
const AddEditBranch = lazy(() => import('../pages/hospital/AddEditBranch'));
const DoctorAvailability = lazy(() => import('../pages/hospital/DoctorAvailability'));
const HospitalAppointments = lazy(() => import('../pages/hospital/HospitalAppointments'));
const HospitalRevenue = lazy(() => import('../pages/hospital/HospitalRevenue'));
const HospitalMessages = lazy(() => import('../pages/hospital/HospitalMessages'));
const HospitalPatientChat = lazy(() => import('../pages/hospital/HospitalPatientChat'));

// Doctor Pages
const DoctorDashboard = lazy(() => import('../pages/doctor/DoctorDashboard'));
const DoctorAppointments = lazy(() => import('../pages/doctor/DoctorAppointments'));
const DoctorAvailabilityPage = lazy(() => import('../pages/doctor/DoctorAvailability'));
const DoctorProfile = lazy(() => import('../pages/doctor/DoctorProfile'));
const DoctorPrescriptionForm = lazy(() => import('../pages/doctor/DoctorPrescriptionForm'));
const DoctorQueueManagement = lazy(() => import('../pages/doctor/QueueManagement'));
const DoctorReferrals = lazy(() => import('../pages/doctor/DoctorReferrals'));
const PatientChat = lazy(() => import('../pages/doctor/PatientChat'));
const PharmacyReferrals = lazy(() => import('../pages/doctor/PharmacyReferrals'));
const LabTestReferrals = lazy(() => import('../pages/doctor/LabTestReferrals'));
const SlotManagement = lazy(() => import('../pages/doctor/SlotManagement'));
const FollowUps = lazy(() => import('../pages/doctor/FollowUps'));
const WorkflowsPage = lazy(() => import('../pages/doctor/WorkflowsPage'));
const PaymentsHub = lazy(() => import('../pages/doctor/PaymentsHub'));
const ChatInbox = lazy(() => import('../pages/doctor/ChatInbox'));
const InquiryChat = lazy(() => import('../pages/doctor/InquiryChat'));
const TemplatesPage = lazy(() => import('../pages/doctor/TemplatesPage'));
const SelectClinicPage = lazy(() => import('../pages/doctor/SelectClinicPage'));

// Admin Pages
const AdminDashboard = lazy(() => import('../pages/admin/AdminDashboard'));
const HospitalManagement = lazy(() => import('../pages/admin/HospitalManagement'));
const AddHospital = lazy(() => import('../pages/admin/AddHospital'));
const HospitalDetail = lazy(() => import('../pages/admin/HospitalDetail'));
const AdminBranchForm = lazy(() => import('../pages/admin/AdminBranchForm'));
const DoctorOverview = lazy(() => import('../pages/admin/DoctorOverview'));
const AdminDoctorForm = lazy(() => import('../pages/admin/AdminDoctorForm'));
const CustomerManagement = lazy(() => import('../pages/admin/CustomerManagement'));
const AppointmentMonitoring = lazy(() => import('../pages/admin/AppointmentMonitoring'));
const RevenueMonitoring = lazy(() => import('../pages/admin/RevenueMonitoring'));
const SystemSettings = lazy(() => import('../pages/admin/SystemSettings'));
const MedicineManagement = lazy(() => import('../pages/admin/MedicineManagement'));
const TestManagement = lazy(() => import('../pages/admin/TestManagement'));
const AdminKnowledgeBase = lazy(() => import('../pages/admin/KnowledgeBase'));
const MedicinePackBuilder = lazy(() => import('../pages/admin/MedicinePackBuilder'));
const TestPanelBuilder = lazy(() => import('../pages/admin/TestPanelBuilder'));

// Public Pages (additional)
const PublicKnowledgeBase = lazy(() => import('../pages/public/KnowledgeBase'));
const QueueKiosk = lazy(() => import('../pages/public/QueueKiosk'));

// Phase B/C — new doctor & hospital pages
const SurgeryReferredAppointments = lazy(() =>
  import('../pages/doctor/SurgeryReferredAppointments')
);
const ExternalReferredAppointments = lazy(() =>
  import('../pages/doctor/ExternalReferredAppointments')
);
const ClinicServicesMaster = lazy(() => import('../pages/hospital/ClinicServicesMaster'));
const ClinicCustomerDB = lazy(() => import('../pages/hospital/ClinicCustomerDB'));
const Pharmacy = lazy(() => import('../pages/hospital/Pharmacy'));
const Diagnostics = lazy(() => import('../pages/hospital/Diagnostics'));
const ClinicCommunications = lazy(() => import('../pages/hospital/ClinicCommunications'));
const CounselingPage = lazy(() => import('../pages/hospital/CounselingPage'));
const HospitalFeedback = lazy(() => import('../pages/hospital/HospitalFeedback'));
const HospitalInvoices = lazy(() => import('../pages/hospital/HospitalInvoices'));
const PartnerManagement = lazy(() => import('../pages/hospital/PartnerManagement'));
const ClinicFollowUps = lazy(() => import('../pages/hospital/ClinicFollowUps'));
const CouponManagement = lazy(() => import('../pages/hospital/CouponManagement'));
const DoctorAvailabilityBoard = lazy(() => import('../pages/hospital/DoctorAvailabilityBoard'));

// Public feedback (shareable link / QR)
const FeedbackPage = lazy(() => import('../pages/public/FeedbackPage'));

// Laboratory module (docs/lab-module/06-frontend.md §2)
const LabHome = lazy(() => import('../pages/lab/LabHome'));
const LabDashboard = lazy(() => import('../pages/lab/LabDashboard'));
const LabSettingsPage = lazy(() => import('../pages/lab/settings/LabSettingsPage'));
const LabUsersPage = lazy(() => import('../pages/lab/users/LabUsersPage'));
const LabPatientsPage = lazy(() => import('../pages/lab/patients/PatientsPage'));
const LabTestCataloguePage = lazy(() => import('../pages/lab/catalogue/TestCataloguePage'));
const LabClientsPage = lazy(() => import('../pages/lab/clients/ClientsPage'));
const LabTariffsPage = lazy(() => import('../pages/lab/tariffs/TariffsPage'));
const LabBookingsPage = lazy(() => import('../pages/lab/bookings/BookingsPage'));
const LabSpecimensPage = lazy(() => import('../pages/lab/specimens/SpecimensPage'));
const LabWorklistPage = lazy(() => import('../pages/lab/worklist/WorklistPage'));
const LabReportsPage = lazy(() => import('../pages/lab/reports/ReportsPage'));
const LabReportTemplatesPage = lazy(() => import('../pages/lab/reportTemplates/ReportTemplatesPage'));
const LabPortalPage = lazy(() => import('../pages/lab/public/LabPortalPage'));
const LabBillingCounterPage = lazy(() => import('../pages/lab/billing/BillingCounterPage'));
const LabCollectionsPage = lazy(() => import('../pages/lab/collections/CollectionsPage'));
const LabAccountsPage = lazy(() => import('../pages/lab/accounts/AccountsPage'));
const LabDayClosePage = lazy(() => import('../pages/lab/dayClose/DayClosePage'));
const LabTpaPage = lazy(() => import('../pages/lab/tpa/TpaPage'));
const LabMyRoundPage = lazy(() => import('../pages/lab/round/MyRoundPage'));
const LabDispatchPage = lazy(() => import('../pages/lab/dispatch/DispatchPage'));
const LabCampaignsPage = lazy(() => import('../pages/lab/campaigns/CampaignsPage'));
const LabInventoryPage = lazy(() => import('../pages/lab/inventory/InventoryPage'));
const LabSendOutsPage = lazy(() => import('../pages/lab/sendouts/SendOutsPage'));
const LabCommissionsPage = lazy(() => import('../pages/lab/commissions/CommissionsPage'));
const LabInstrumentsPage = lazy(() => import('../pages/lab/instruments/InstrumentsPage'));
const LabQcPage = lazy(() => import('../pages/lab/qc/QcPage'));
const LabAnalyticsPage = lazy(() => import('../pages/lab/analytics/AnalyticsPage'));
const LabWorkflowsPage = lazy(() => import('../pages/lab/workflows/WorkflowsPage'));
const LabTrackingPage = lazy(() => import('../pages/lab/public/LabTrackingPage'));
const CustomerLabReports = lazy(() => import('../pages/customer/CustomerLabReports'));
const DoctorLabOrders = lazy(() => import('../pages/doctor/DoctorLabOrders'));
const LabsAdmin = lazy(() => import('../pages/admin/LabsAdmin'));

const AppRoutes = () => {
  return (
    <Suspense fallback={<LoadingSpinner fullScreen message="Loading page..." />}>
      <Routes>
        {/* Public Routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/doctors" element={<DoctorListingPage />} />
          <Route path="/doctors/:id" element={<DoctorProfilePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/payment" element={<PaymentPage />} />
          <Route path="/booking-confirmation" element={<BookingConfirmationPage />} />
          <Route path="/knowledge" element={<PublicKnowledgeBase />} />
        </Route>

        {/* Standalone public routes — no PublicLayout chrome (designed for a kiosk TV) */}
        <Route path="/queue/kiosk" element={<QueueKiosk />} />
        <Route path="/queue/kiosk/:doctorId" element={<QueueKiosk />} />
        {/* Laboratory patient portal: token-addressed, no login, no layout. */}
        <Route path="/lab/portal/:token" element={<LabPortalPage />} />
        {/* Home-collection tracking for the patient (fix 17): token-addressed, no login, no layout. */}
        <Route path="/lab/track/:token" element={<LabTrackingPage />} />

        {/* Public feedback capture (shareable link / QR) */}
        <Route path="/feedback/:appointmentId" element={<FeedbackPage />} />

        {/* Customer Routes */}
        <Route element={<ProtectedRoute allowedRoles={['customer']} />}>
          <Route element={<DashboardLayout />}>
            <Route path="/customer/dashboard" element={<CustomerDashboard />} />
            <Route path="/customer/appointments" element={<CustomerAppointments />} />
            <Route path="/customer/appointments/:id" element={<AppointmentDetail />} />
            <Route path="/customer/payments" element={<PaymentHistory />} />
            <Route path="/customer/prescriptions" element={<CustomerPrescriptions />} />
            <Route path="/customer/prescriptions/:appointmentId" element={<PrescriptionDetail />} />
            <Route path="/customer/invoices" element={<CustomerInvoices />} />
            <Route path="/customer/lab-reports" element={<CustomerLabReports />} />
            <Route path="/customer/feedback" element={<CustomerFeedback />} />
            <Route path="/customer/notifications" element={<NotificationsPage />} />
            <Route path="/customer/profile" element={<CustomerProfile />} />
          </Route>
        </Route>

        {/* Notifications for every other role: doctors (lab results, critical values),
            lab staff and hospital admins (new lab orders, bills awaiting approval). */}
        <Route element={<ProtectedRoute allowedRoles={['doctor', 'hospital_admin', 'lab_staff', 'admin']} />}>
          <Route element={<DashboardLayout />}>
            <Route path="/notifications" element={<NotificationsPage />} />
          </Route>
        </Route>

        {/* Doctor Routes */}
        <Route element={<ProtectedRoute allowedRoles={['doctor']} />}>
          {/* Full-screen clinic selector (no dashboard chrome) */}
          <Route path="/doctor/select-clinic" element={<SelectClinicPage />} />
          <Route element={<DashboardLayout />}>
            <Route path="/doctor/dashboard" element={<DoctorDashboard />} />
            <Route path="/doctor/appointments" element={<DoctorAppointments />} />
            <Route path="/doctor/availability" element={<DoctorAvailabilityPage />} />
            <Route path="/doctor/appointments/:appointmentId/prescribe" element={<DoctorPrescriptionForm />} />
            <Route path="/doctor/queue" element={<DoctorQueueManagement />} />
            <Route path="/doctor/referrals" element={<DoctorReferrals />} />
            <Route path="/doctor/pharmacy" element={<PharmacyReferrals />} />
            <Route path="/doctor/lab-tests" element={<LabTestReferrals />} />
            <Route path="/doctor/lab-orders" element={<DoctorLabOrders />} />
            <Route path="/doctor/slots" element={<SlotManagement />} />
            <Route path="/doctor/follow-ups" element={<FollowUps />} />
            <Route path="/doctor/surgery-referrals" element={<SurgeryReferredAppointments />} />
            <Route path="/doctor/external-referrals" element={<ExternalReferredAppointments />} />
            <Route path="/doctor/payments" element={<PaymentsHub />} />
            <Route
              path="/doctor/patient-payments"
              element={<Navigate to="/doctor/payments?tab=patients" replace />}
            />
            <Route path="/doctor/messages" element={<ChatInbox />} />
            <Route path="/doctor/appointments/:appointmentId/chat" element={<PatientChat />} />
            <Route path="/doctor/inquiries/:contactId/chat" element={<InquiryChat />} />
            <Route path="/doctor/appointments/:appointmentId/templates" element={<TemplatesPage />} />
            <Route path="/doctor/workflows" element={<WorkflowsPage />} />
            <Route path="/doctor/profile" element={<DoctorProfile />} />
          </Route>
        </Route>

        {/* Hospital Admin Routes */}
        <Route element={<ProtectedRoute allowedRoles={['hospital_admin']} />}>
          <Route element={<DashboardLayout />}>
            <Route path="/hospital/dashboard" element={<HospitalDashboard />} />
            <Route path="/hospital/profile" element={<HospitalProfile />} />
            <Route path="/hospital/doctors" element={<DoctorManagement />} />
            <Route path="/hospital/doctors/add" element={<AddEditDoctor />} />
            <Route path="/hospital/doctors/:id/edit" element={<AddEditDoctor />} />
            <Route path="/hospital/branches" element={<BranchManagement />} />
            <Route path="/hospital/branches/add" element={<AddEditBranch />} />
            <Route path="/hospital/branches/:id/edit" element={<AddEditBranch />} />
            <Route path="/hospital/availability" element={<DoctorAvailability />} />
            <Route path="/hospital/appointments" element={<HospitalAppointments />} />
            <Route path="/hospital/payment-gateway" element={<PaymentGatewaySettings />} />
            <Route path="/hospital/insurance" element={<InsuranceSearch />} />
            <Route path="/hospital/customers" element={<ClinicCustomerDB />} />
            <Route path="/hospital/communications" element={<ClinicCommunications />} />
            <Route path="/hospital/messages" element={<HospitalMessages />} />
            <Route path="/hospital/appointments/:appointmentId/chat" element={<HospitalPatientChat />} />
            <Route path="/hospital/counseling" element={<CounselingPage />} />
            <Route path="/hospital/doctor-status" element={<DoctorAvailabilityBoard />} />
            <Route path="/hospital/feedback" element={<HospitalFeedback />} />
            <Route path="/hospital/invoices" element={<HospitalInvoices />} />
            <Route path="/hospital/partners" element={<PartnerManagement />} />
            <Route path="/hospital/follow-ups" element={<ClinicFollowUps />} />
            <Route path="/hospital/coupons" element={<CouponManagement />} />
            <Route path="/hospital/services-clinic" element={<ClinicServicesMaster />} />
            <Route path="/hospital/revenue" element={<HospitalRevenue />} />
            {/* Consolidated catalog modules */}
            <Route path="/hospital/pharmacy" element={<Pharmacy />} />
            <Route path="/hospital/diagnostics" element={<Diagnostics />} />
            {/* Legacy URLs — keep working as deep-links into the new tabbed pages */}
            <Route
              path="/hospital/medicines"
              element={<Navigate to="/hospital/pharmacy?tab=items" replace />}
            />
            <Route
              path="/hospital/medicine-packs"
              element={<Navigate to="/hospital/pharmacy?tab=packs" replace />}
            />
            <Route
              path="/hospital/tests"
              element={<Navigate to="/hospital/diagnostics?tab=tests" replace />}
            />
            <Route
              path="/hospital/test-panels"
              element={<Navigate to="/hospital/diagnostics?tab=panels" replace />}
            />
            <Route path="/hospital/knowledge" element={<AdminKnowledgeBase />} />
          </Route>
        </Route>

        {/* Platform Admin Routes */}
        <Route element={<ProtectedRoute allowedRoles={['admin']} />}>
          <Route element={<DashboardLayout />}>
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/hospitals" element={<HospitalManagement />} />
            <Route path="/admin/hospitals/add" element={<AddHospital />} />
            <Route path="/admin/hospitals/:id" element={<HospitalDetail />} />
            <Route path="/admin/hospitals/:id/edit" element={<AddHospital />} />
            <Route path="/admin/hospitals/:id/branches/add" element={<AdminBranchForm />} />
            <Route path="/admin/hospitals/:id/branches/:branchId/edit" element={<AdminBranchForm />} />
            <Route path="/admin/doctors" element={<DoctorOverview />} />
            <Route path="/admin/doctors/add" element={<AdminDoctorForm />} />
            <Route path="/admin/doctors/:doctorId/edit" element={<AdminDoctorForm />} />
            <Route path="/admin/customers" element={<CustomerManagement />} />
            <Route path="/admin/appointments" element={<AppointmentMonitoring />} />
            <Route path="/admin/revenue" element={<RevenueMonitoring />} />
            <Route path="/admin/medicines" element={<MedicineManagement />} />
            <Route path="/admin/medicine-packs" element={<MedicinePackBuilder />} />
            <Route path="/admin/tests" element={<TestManagement />} />
            <Route path="/admin/test-panels" element={<TestPanelBuilder />} />
            <Route path="/admin/knowledge" element={<AdminKnowledgeBase />} />
            <Route path="/admin/settings" element={<SystemSettings />} />
            <Route path="/admin/labs" element={<LabsAdmin />} />
          </Route>
        </Route>

        {/* Laboratory — lab staff, hospital admins, platform admins (?hospital) */}
        <Route element={<ProtectedRoute allowedRoles={['lab_staff', 'hospital_admin', 'admin']} />}>
          <Route element={<DashboardLayout />}>
            <Route path="/lab" element={<LabHome />} />
            <Route
              path="/lab/dashboard"
              element={<LabGuard permission="dashboard"><LabDashboard /></LabGuard>}
            />
            <Route
              path="/lab/users"
              element={<LabGuard permission="users"><LabUsersPage /></LabGuard>}
            />
            <Route path="/lab/staff" element={<Navigate to="/lab/users" replace />} />
            <Route
              path="/lab/patients"
              element={<LabGuard permission="patients"><LabPatientsPage /></LabGuard>}
            />
            <Route
              path="/lab/tests"
              element={<LabGuard permission="tests"><LabTestCataloguePage /></LabGuard>}
            />
            <Route
              path="/lab/clients"
              element={<LabGuard permission="clients"><LabClientsPage /></LabGuard>}
            />
            <Route
              path="/lab/tariffs"
              element={<LabGuard permission="tariffs"><LabTariffsPage /></LabGuard>}
            />
            <Route
              path="/lab/settings"
              element={<LabGuard permission="settings"><LabSettingsPage /></LabGuard>}
            />
            <Route
              path="/lab/bookings"
              element={<LabGuard permission={['bookings', 'homecollection']}><LabBookingsPage /></LabGuard>}
            />
            <Route
              path="/lab/specimens"
              element={<LabGuard permission="lab"><LabSpecimensPage /></LabGuard>}
            />
            <Route
              path="/lab/worklist"
              element={<LabGuard permission="worklist"><LabWorklistPage /></LabGuard>}
            />
            <Route
              path="/lab/reports"
              element={<LabGuard permission="reports"><LabReportsPage /></LabGuard>}
            />
            <Route
              path="/lab/report-templates"
              element={<LabGuard permission="lab"><LabReportTemplatesPage /></LabGuard>}
            />
            <Route
              path="/lab/billing"
              element={<LabGuard permission={['billing', 'billapproval']}><LabBillingCounterPage /></LabGuard>}
            />
            <Route
              path="/lab/collections"
              element={<LabGuard permission="billing"><LabCollectionsPage /></LabGuard>}
            />
            <Route
              path="/lab/accounts"
              element={<LabGuard permission="accounts"><LabAccountsPage /></LabGuard>}
            />
            <Route
              path="/lab/day-close"
              element={<LabGuard permission="dayclose"><LabDayClosePage /></LabGuard>}
            />
            <Route
              path="/lab/tpa"
              element={<LabGuard permission="tpa"><LabTpaPage /></LabGuard>}
            />
            <Route
              path="/lab/round"
              element={<LabGuard permission="homecollection"><LabMyRoundPage /></LabGuard>}
            />
            <Route
              path="/lab/dispatch"
              element={<LabGuard permission="dispatch"><LabDispatchPage /></LabGuard>}
            />
            <Route
              path="/lab/campaigns"
              element={<LabGuard permission="campaigns"><LabCampaignsPage /></LabGuard>}
            />
            <Route
              path="/lab/inventory"
              element={<LabGuard permission="inventory"><LabInventoryPage /></LabGuard>}
            />
            <Route
              path="/lab/send-outs"
              element={<LabGuard permission="sendouts"><LabSendOutsPage /></LabGuard>}
            />
            <Route
              path="/lab/commissions"
              element={<LabGuard permission="accounts"><LabCommissionsPage /></LabGuard>}
            />
            <Route
              path="/lab/instruments"
              element={<LabGuard permission="instruments"><LabInstrumentsPage /></LabGuard>}
            />
            <Route
              path="/lab/qc"
              element={<LabGuard permission="lab"><LabQcPage /></LabGuard>}
            />
            <Route
              path="/lab/analytics"
              element={<LabGuard permission="analytics"><LabAnalyticsPage /></LabGuard>}
            />
            <Route
              path="/lab/workflows"
              element={<LabGuard permission="workflows"><LabWorkflowsPage /></LabGuard>}
            />
            {/* An unknown lab path falls back to the lab dashboard. */}
            <Route path="/lab/*" element={<Navigate to="/lab/dashboard" replace />} />
          </Route>
        </Route>

        {/* 404 Catch-All */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
